// ============================================
// PermissionContext.js
// React Context for Permission Management
// Place in: src/contexts/PermissionContext.js
// ============================================

import React, { createContext, useContext, useCallback, useMemo } from 'react';

// Create the context
const PermissionContext = createContext(null);

/**
 * PermissionProvider - Wraps your app to provide permission checking
 * 
 * Usage in App.js:
 * <PermissionProvider user={user}>
 *   <YourApp />
 * </PermissionProvider>
 */
export const PermissionProvider = ({ children, user }) => {
  // Extract permissions and roles from user object
  // Memoized so the fallback {} / [] don't create new references every render,
  // which would invalidate every useCallback/useMemo below.
  const permissions = useMemo(() => user?.permissions || {}, [user]);
  const roles = useMemo(() => user?.roles || [], [user]);

  /**
   * Check if user has a specific permission
   * @param {string} moduleCode - e.g., 'bus', 'afterschool'
   * @param {string} featureCode - e.g., 'enrollments', 'group_rentals'
   * @param {string} actionCode - e.g., 'view', 'create', 'edit', 'delete'
   * @returns {boolean}
   */
  const hasPermission = useCallback((moduleCode, featureCode, actionCode = 'view') => {
    // GlobalAdmin always has access (fallback for backwards compatibility)
    if (roles.includes('GlobalAdmin')) {
      return true;
    }

    // Check granular permissions
    const modulePerms = permissions[moduleCode];
    if (!modulePerms) return false;

    const featurePerms = modulePerms[featureCode];
    if (!featurePerms) return false;

    return featurePerms.includes(actionCode);
  }, [permissions, roles]);

  /**
   * Check if user can access a module at all (has any permission in that module)
   * @param {string} moduleCode 
   * @returns {boolean}
   */
  const canAccessModule = useCallback((moduleCode) => {
    // GlobalAdmin always has access
    if (roles.includes('GlobalAdmin')) {
      return true;
    }

    // Check if user has any permissions in this module
    return !!permissions[moduleCode] && Object.keys(permissions[moduleCode]).length > 0;
  }, [permissions, roles]);

  /**
   * Check if user can access a specific feature (any action)
   * @param {string} moduleCode 
   * @param {string} featureCode 
   * @returns {boolean}
   */
  const canAccessFeature = useCallback((moduleCode, featureCode) => {
    if (roles.includes('GlobalAdmin')) {
      return true;
    }

    const modulePerms = permissions[moduleCode];
    if (!modulePerms) return false;

    const featurePerms = modulePerms[featureCode];
    return !!featurePerms && featurePerms.length > 0;
  }, [permissions, roles]);

  /**
   * Get all allowed actions for a feature
   * @param {string} moduleCode 
   * @param {string} featureCode 
   * @returns {string[]}
   */
  const getAllowedActions = useCallback((moduleCode, featureCode) => {
    if (roles.includes('GlobalAdmin')) {
      // Return all possible actions for GlobalAdmin
      return ['view', 'create', 'edit', 'delete', 'print', 'export', 'process', 'cancel', 'configure'];
    }

    const modulePerms = permissions[moduleCode];
    if (!modulePerms) return [];

    return modulePerms[featureCode] || [];
  }, [permissions, roles]);

  /**
   * Check if user has a specific role (backwards compatibility)
   * @param {string} roleName 
   * @returns {boolean}
   */
  const hasRole = useCallback((roleName) => {
    return roles.includes(roleName);
  }, [roles]);

  /**
   * Check multiple permissions at once
   * @param {Array} checks - Array of {module, feature, action}
   * @returns {boolean} - True if ALL permissions are granted
   */
  const hasAllPermissions = useCallback((checks) => {
    return checks.every(({ module, feature, action }) => 
      hasPermission(module, feature, action || 'view')
    );
  }, [hasPermission]);

  /**
   * Check if user has ANY of the specified permissions
   * @param {Array} checks - Array of {module, feature, action}
   * @returns {boolean} - True if ANY permission is granted
   */
  const hasAnyPermission = useCallback((checks) => {
    return checks.some(({ module, feature, action }) => 
      hasPermission(module, feature, action || 'view')
    );
  }, [hasPermission]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    permissions,
    roles,
    hasPermission,
    canAccessModule,
    canAccessFeature,
    getAllowedActions,
    hasRole,
    hasAllPermissions,
    hasAnyPermission,
    isGlobalAdmin: roles.includes('GlobalAdmin')
  }), [
    permissions, 
    roles, 
    hasPermission, 
    canAccessModule, 
    canAccessFeature, 
    getAllowedActions, 
    hasRole,
    hasAllPermissions,
    hasAnyPermission
  ]);

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
};

/**
 * Hook to use permissions in any component
 * 
 * Usage:
 * const { hasPermission, canAccessModule } = usePermissions();
 * 
 * if (hasPermission('bus', 'group_rentals', 'create')) {
 *   // Show create button
 * }
 */
export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

/**
 * PermissionGate - Conditionally render children based on permission
 * 
 * Usage:
 * <PermissionGate module="bus" feature="group_rentals" action="create">
 *   <button>Create Rental</button>
 * </PermissionGate>
 * 
 * With fallback:
 * <PermissionGate module="bus" feature="group_rentals" action="delete" fallback={<span>No access</span>}>
 *   <button>Delete</button>
 * </PermissionGate>
 */
export const PermissionGate = ({ 
  module, 
  feature, 
  action = 'view', 
  fallback = null, 
  children 
}) => {
  const { hasPermission } = usePermissions();

  if (hasPermission(module, feature, action)) {
    return children;
  }

  return fallback;
};

/**
 * ModuleGate - Conditionally render based on module access
 * 
 * Usage:
 * <ModuleGate module="bus">
 *   <BusModule />
 * </ModuleGate>
 */
export const ModuleGate = ({ module, fallback = null, children }) => {
  const { canAccessModule } = usePermissions();

  if (canAccessModule(module)) {
    return children;
  }

  return fallback;
};

/**
 * FeatureGate - Conditionally render based on feature access (any action)
 * 
 * Usage:
 * <FeatureGate module="afterschool" feature="renewal_settings">
 *   <RenewalSettingsTab />
 * </FeatureGate>
 */
export const FeatureGate = ({ module, feature, fallback = null, children }) => {
  const { canAccessFeature } = usePermissions();

  if (canAccessFeature(module, feature)) {
    return children;
  }

  return fallback;
};

/**
 * RoleGate - Conditionally render based on role (backwards compatibility)
 * 
 * Usage:
 * <RoleGate roles={['GlobalAdmin', 'Accounting']}>
 *   <AdminOnlyContent />
 * </RoleGate>
 */
export const RoleGate = ({ roles: requiredRoles, fallback = null, children }) => {
  const { roles } = usePermissions();

  const hasRequiredRole = requiredRoles.some(role => roles.includes(role));

  if (hasRequiredRole) {
    return children;
  }

  return fallback;
};

export default PermissionContext;
